import {
  ListStyleType,
  someList,
  someTodoList,
  toggleList,
} from "@platejs/list";
import { ChevronDown, List, ListOrdered, ListTodo } from "lucide-react";
import { useEditorRef, useEditorSelector } from "platejs/react";
import { memo, useMemo, useState } from "react";
import { RedoButton } from "../../arbiter-components/buttons/redo-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "../../arbiter-components/command-menu/redo-command-menu";
import {
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from "../ui/dropdown-menu";
import { useToolbarOverflow } from "./toolbar-overflow";

const LIST_ITEMS = [
  { Icon: List, text: "Bulleted list", listStyleType: ListStyleType.Disc },
  {
    Icon: ListOrdered,
    text: "Numbered list",
    listStyleType: ListStyleType.Decimal,
  },
  { Icon: ListTodo, text: "Todo list", listStyleType: "todo" as const },
] as const;

export const ListToolbarButton = memo(function ListToolbarButton() {
  const editor = useEditorRef();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState<HTMLButtonElement | null>(null);

  const isBulleted = useEditorSelector(
    (editor) =>
      someList(editor, [
        ListStyleType.Disc,
        ListStyleType.Circle,
        ListStyleType.Square,
      ]),
    [],
  );
  const isNumbered = useEditorSelector(
    (editor) =>
      someList(editor, [
        ListStyleType.Decimal,
        ListStyleType.LowerAlpha,
        ListStyleType.UpperAlpha,
        ListStyleType.LowerRoman,
        ListStyleType.UpperRoman,
      ]),
    [],
  );
  const isTodo = useEditorSelector((editor) => someTodoList(editor), []);

  const ActiveIcon = isBulleted
    ? List
    : isNumbered
      ? ListOrdered
      : isTodo
        ? ListTodo
        : List;

  const items: RedoCommandMenuItem[] = useMemo(
    () =>
      LIST_ITEMS.map(({ Icon, text, listStyleType }) => ({
        Icon,
        text,
        onClick: () => {
          toggleList(editor, { listStyleType });
          editor.tf.focus();
        },
      })),
    [editor],
  );

  const { isOverflow } = useToolbarOverflow();

  if (isOverflow) {
    return (
      <DropdownMenuSub>
        <DropdownMenuSubTrigger>
          <List />
          Lists
        </DropdownMenuSubTrigger>
        <DropdownMenuSubContent>
          {LIST_ITEMS.map(({ Icon, text, listStyleType }) => (
            <DropdownMenuItem
              key={text}
              onSelect={() => {
                toggleList(editor, { listStyleType });
                editor.tf.focus();
              }}
            >
              <Icon />
              {text}
            </DropdownMenuItem>
          ))}
        </DropdownMenuSubContent>
      </DropdownMenuSub>
    );
  }

  return (
    <>
      <RedoButton
        hierarchy="tertiary"
        IconLeading={ActiveIcon}
        IconTrailing={ChevronDown}
        onClick={() => setOpen((prev) => !prev)}
        pressed={isBulleted || isNumbered || isTodo}
        ref={setAnchor}
        size="sm"
        text=""
      />
      <RedoCommandMenu
        anchor={anchor}
        items={items}
        open={open}
        placement="bottom-start"
        setOpen={setOpen}
      />
    </>
  );
});

// Keep individual exports for backwards compatibility
export const BulletedListToolbarButton = ListToolbarButton;
export const NumberedListToolbarButton = ListToolbarButton;
export const TodoListToolbarButton = ListToolbarButton;
